import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllCopyConfigs } from '@/api/copyConfig';

/**
 * Fetches all dynamic copy configs from the server and injects them into
 * i18next as a dedicated `copy` namespace.
 *
 * Usage in a component:
 *   const { t } = useTranslation('copy');
 *   t('home_banner_title')   // returns the zh/en string based on current language
 */
export const useCopyConfig = () => {
  const { i18n } = useTranslation();

  const loadCopyConfigs = useCallback(async (type?: string) => {
    const configs = await getAllCopyConfigs(type);
    if (!configs.length) return;

    // Build flat resource bundles keyed by the copy config `key` field
    const zhBundle: Record<string, string> = {};
    const enBundle: Record<string, string> = {};

    for (const config of configs) {
      zhBundle[config.key] = config.zh;
      enBundle[config.key] = config.en;
    }

    // Add (or merge) resources into the `copy` namespace for both locales
    i18n.addResourceBundle('zh', 'copy', zhBundle, true, true);
    i18n.addResourceBundle('en', 'copy', enBundle, true, true);
  }, [i18n]);

  return { loadCopyConfigs };
};
